import {
  type CheckboxChangeEventType,
  type DropDownResult,
  DynamicForm,
  type DynamicFormFieldType,
  type DynamicFormRef,
  DynamicFormRequiredType,
  type JsonDataWrapper,
  type DynamicFormSettings as OdinComponentsDynamicFormSettings,
  type OdinDataRetrieval,
  type OdinDataRetrievalOptions,
  OdinDataSender,
  type OdinKey,
} from '@myosh/odin-components';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type { CustomDynamicFormFieldType } from '../../../@types/forms';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  selectAutoPopulateHierarchyFromPersonFieldId,
  selectAutoPopulateHierarchyFromUserHierarchy,
  selectFieldGroups,
  selectHierarchyDisplayType,
  selectHierarchyEnableAdvance,
  selectHierarchyHiddenInReadMode,
  setSelectedGroupId,
} from '../../../redux/slices/form-builder-slice';
import { forceAssert } from '../../../utils/type-guards';
import CloseActiveItemButton from '../../common/close-active-item-button';
import HfDropDown from '../../fields/custom-drop-downs/hf-drop-down.component';
import { groupDisplayTypes } from '../util/group-details.util';

const dataSettings = {
  valueField: 'value',
  textField: 'text',
};

const initialValueOptions = [
  { text: 'Autopopulate from the user creating the record', value: 'USER' },
  { text: 'Autopopulate from a field on the form', value: 'PERSON_FIELD' },
  { text: 'Leave blank', value: 'BLANK' },
];

const hierarchySectionFormSettings = (initialValue?: DropDownResult): OdinComponentsDynamicFormSettings => {
  return {
    id: 'HierarchySectionPropertiesGroup',
    caption: '',
    fields: [
      {
        id: 'hierarchyHiddenInReadMode',
        fieldType: 'CHECKBOX',
        fieldName: 'hierarchyHiddenInReadMode',
        caption: 'Hidden in Read Mode',
        permissions: { read: true, edit: true },
        required: DynamicFormRequiredType.False,
        position: 0,
      },
      {
        id: 'hierarchyEnableAdvance',
        fieldType: 'CHECKBOX',
        fieldName: 'hierarchyEnableAdvance',
        caption: 'Enable Advanced',
        permissions: { read: true, edit: true },
        required: DynamicFormRequiredType.False,
        position: 1,
      },
      {
        id: 'hierarchyDisplayType',
        fieldType: 'OPTIONGROUP',
        fieldName: 'hierarchyDisplayType',
        caption: 'Default Display Option',
        required: DynamicFormRequiredType.False,
        position: 2,
        dynamicProperties: {
          lookupType: 'NONE',
          orientation: 'VERTICAL',
        },
        dataSettings: dataSettings,
        hidden: false,
        permissions: { read: true, edit: true },
      },
      {
        id: 'initialValue',
        fieldType: 'COMBOBOX',
        fieldName: 'initialValue',
        caption: 'Initial Value',
        dataSettings: dataSettings,
        permissions: { read: true, edit: true },
        required: DynamicFormRequiredType.True,
        position: 3,
      },
      {
        id: 'personField',
        fieldType: 'COMBOBOX',
        fieldName: 'personField',
        caption: 'Person Field',
        dataSettings: dataSettings,
        required: DynamicFormRequiredType.True,
        permissions: { read: true, edit: true },
        position: 4,
        hidden: initialValue && initialValue.value !== 'PERSON_FIELD',
      },
    ],
    fieldValueConverters: {
      CHECKBOX: (value) => {
        const convertedValue = forceAssert<CheckboxChangeEventType>(value);
        return convertedValue.checked;
      },
    },
    customFieldComponents: {
      COMBOBOX: HfDropDown,
    },
  };
};

function FormBuilderHierarchySectionDetails() {
  const dispatch = useAppDispatch();

  const fieldGroups = useAppSelector(selectFieldGroups);
  const hierarchyEnableAdvance = useAppSelector(selectHierarchyEnableAdvance);
  const autoPopulateFromPersonFieldId = useAppSelector(selectAutoPopulateHierarchyFromPersonFieldId);
  const autoPopulateFromUserHierarchy = useAppSelector(selectAutoPopulateHierarchyFromUserHierarchy);
  const hiddenInReadMode = useAppSelector(selectHierarchyHiddenInReadMode);
  const hierarchyDisplayType = useAppSelector(selectHierarchyDisplayType);

  const [initialValue, setInitialValue] = React.useState<DropDownResult>();

  const formIdRef = useRef(crypto.randomUUID());
  const dynamicFormRef = useRef<DynamicFormRef>(null);

  const personFields = useMemo(
    () =>
      fieldGroups
        ?.map((group) =>
          group.fields?.filter((field) => (field.fieldType as CustomDynamicFormFieldType) === 'PERSONFIELD')
        )
        .flat()
        .filter((f) => f)
        .map((field) => ({
          text: field?.fieldCaption?.translations?.[0]?.value ?? '',
          value: field?.id ?? field?.uuid,
        })),
    [fieldGroups]
  );

  const formSettings = useMemo(() => hierarchySectionFormSettings(initialValue), [initialValue]);

  const loadGroupData = useCallback(() => {
    const prsonFieldValue = autoPopulateFromPersonFieldId;
    const userHierarchyValue = autoPopulateFromUserHierarchy;

    const initialValue = prsonFieldValue
      ? initialValueOptions[1]
      : userHierarchyValue
        ? initialValueOptions[0]
        : initialValueOptions[2];

    setInitialValue(initialValue);

    dynamicFormRef.current?.loadData({
      hierarchyHiddenInReadMode: hiddenInReadMode,
      hierarchyEnableAdvance: hierarchyEnableAdvance,
      hierarchyDisplayType: groupDisplayTypes.find((type) => hierarchyDisplayType === type.value),
      initialValue: initialValue,
      personField: personFields?.find((p) => p.value === prsonFieldValue),
    });
  }, [
    personFields,
    autoPopulateFromPersonFieldId,
    autoPopulateFromUserHierarchy,
    hiddenInReadMode,
    hierarchyEnableAdvance,
    hierarchyDisplayType,
  ]);

  useEffect(() => {
    dynamicFormRef.current?.resetFormState({});
    loadGroupData();
  }, [loadGroupData]);

  const onCloseClick = useCallback(() => {
    dispatch(setSelectedGroupId(undefined));
  }, [dispatch]);

  const dataRetrieval: OdinDataRetrieval = useMemo(() => {
    return {
      getData: (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
        if (options?.fieldId === 'initialValue' && options?.fieldType === 'COMBOBOX' && options?.page === 1) {
          subscriber.sendData({ data: initialValueOptions.map((o) => ({ text: o.text, value: o.value })) });
        } else if (options?.fieldId === 'hierarchyDisplayType' && options?.page === 1) {
          subscriber.sendData({
            data: groupDisplayTypes,
          });
        } else if (options?.fieldId === 'personField' && options?.page === 1) {
          subscriber.sendData({ data: personFields });
        } else {
          subscriber.sendData();
        }
      },
    };
  }, [personFields]);

  const onFieldChanged = (
    fieldId: OdinKey,
    fieldName: string,
    fieldType: DynamicFormFieldType,
    fieldValue: unknown
  ) => {
    if ('initialValue' === fieldName) {
      setInitialValue(fieldValue as DropDownResult);
    }
  };

  return (
    <div className="flex w-full flex-col">
      <div className="flex items-center justify-between py-2">
        <h2 className="text-lg font-semibold">Hierarchy Section Properties</h2>
        <CloseActiveItemButton onClick={onCloseClick} />
      </div>

      {formSettings && (
        <DynamicForm
          ref={dynamicFormRef}
          dynamicFormId={formIdRef.current}
          settings={formSettings}
          onSubmit={() => {}}
          data={{}}
          dataRetrieval={dataRetrieval}
          formMode="all"
          onFieldChanged={onFieldChanged}
        />
      )}
    </div>
  );
}

export default React.memo(FormBuilderHierarchySectionDetails);
